import React, { createContext, useContext, useMemo } from 'react';
import type { ConsentDecision } from '@/utils/analytics/euConsent';

/**
 * WEB-1330 п.2 (вариант А): блок cookie_consent (рендерится глубоко внутри
 * QuizScreenWizard/PaywallScreenWizard → PublicBlockRenderer →
 * PublicBlockRendererExtended) сам пишет решение через euConsent
 * (writeConsentDecision) — это работает независимо от контекста. Но у
 * страницы (PublicQuiz.tsx/PublicPaywall.tsx) уже есть handleConsent,
 * который ПОВЕРХ записи ещё и мгновенно дозагружает пиксели (loadPixels +
 * fireMetaBrowserEvent) без перезагрузки страницы — так уже ведёт себя
 * системный ConsentBanner. Этот контекст даёт блоку доступ ровно к этому же
 * handleConsent, без прокидывания коллбэка через пропсы всех промежуточных
 * компонентов (тот же приём, что FormValuesContext для values/setValue).
 *
 * Вне провайдера — безопасный no-op (блок продолжает писать решение сам,
 * просто без мгновенной дозагрузки пикселей — так ведут себя editor-превью
 * и любой рендер вне PublicQuiz/PublicPaywall, например существующие тесты
 * портала).
 */
export interface ConsentActionsContextValue {
  onConsentDecision: (decision: ConsentDecision) => void;
}

const noop = (): void => {
  /* no-op outside a ConsentActionsProvider */
};

const ConsentActionsContext = createContext<ConsentActionsContextValue>({
  onConsentDecision: noop,
});

interface ConsentActionsProviderProps {
  onConsentDecision: (decision: ConsentDecision) => void;
  children: React.ReactNode;
}

export function ConsentActionsProvider({
  onConsentDecision,
  children,
}: ConsentActionsProviderProps): React.ReactElement {
  const ctx = useMemo<ConsentActionsContextValue>(
    () => ({ onConsentDecision }),
    [onConsentDecision],
  );
  return (
    <ConsentActionsContext.Provider value={ctx}>
      {children}
    </ConsentActionsContext.Provider>
  );
}

export function useConsentActions(): ConsentActionsContextValue {
  return useContext(ConsentActionsContext);
}
